/**
 * Класичний React ErrorBoundary (Sprint 10).
 *
 * Загортає дочірні компоненти і не дає JS-помилкам всередині них
 * викинути весь app у "білу сторінку". При краху рендериться `fallback`
 * (або генеричний меседж) + помилка йде у console.
 *
 * Використання — точково навколо ризикових компонентів (камера-сканер,
 * чужі lib з невизначеною поведінкою). НЕ обгортати весь app — це
 * приховає реальні баги під час dev.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Fallback UI або колбек що отримує помилку. */
  fallback?: ReactNode | ((err: Error, reset: () => void) => ReactNode);
  /** Викликається при кожному краху — для toast/logging. */
  onError?: (err: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;

    return (
      <div data-reload-ok className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3">
          <h2 className="text-lg font-bold text-mes-danger">Сталася помилка</h2>
          <p className="text-sm text-mes-muted break-words">{error.message}</p>
          <button
            type="button"
            onClick={this.reset}
            className="w-full h-12 bg-mes-primary text-white rounded-lg font-semibold"
          >
            Закрити
          </button>
        </div>
      </div>
    );
  }
}
